import { FC } from "react";

import ButtonBase from "@mui/material/ButtonBase";
import Box from "@mui/material/Box";
import { LAST_DAY_OF_MONTH, MAX_CHARGE_DAY_OF_MONTH } from "@simplyk/common";

import { ChargeDayOfMonth } from "../helpers/useEditChargeDate";

import { useTranslate } from "@/common/hooks/useTranslate";

const DAYS = Array.from({ length: MAX_CHARGE_DAY_OF_MONTH }, (_, index) => index + 1);

interface DayGridProps {
  selectedDay: ChargeDayOfMonth | null;
  currentDay: ChargeDayOfMonth | null;
  onSelectDay: (day: ChargeDayOfMonth) => void;
  showLastDay?: boolean;
}

export const DayGrid: FC<DayGridProps> = ({ selectedDay, currentDay, onSelectDay, showLastDay = false }) => {
  const { t } = useTranslate();
  const isLastDaySelected = selectedDay === LAST_DAY_OF_MONTH;

  return (
    <Box
      sx={(theme) => ({
        display: "grid",
        gridTemplateColumns: "repeat(7, 36px)",
        gridAutoRows: "36px",
        gap: "2px",
        justifyContent: "center",
        paddingY: theme.spacing(1.5),
      })}
    >
      {DAYS.map((day) => {
        const isSelected = day === selectedDay;
        const isCurrent = day === currentDay;
        return (
          <ButtonBase
            key={day}
            data-test={`edit-charge-date-day-${day}`}
            aria-pressed={isSelected}
            onClick={() => onSelectDay(day)}
            sx={(theme) => ({
              width: 36,
              height: 36,
              borderRadius: "18px",
              ...theme.typography.body2,
              color: isSelected ? theme.palette.text.reverse.intense : theme.palette.text.neutral.moderate,
              backgroundColor: isSelected ? theme.palette.surface.brand.intense : "transparent",
              border: !isSelected && isCurrent ? `1px solid ${theme.palette.border.neutral.quiet}` : "none",
            })}
          >
            {day}
          </ButtonBase>
        );
      })}
      {showLastDay && (
        <ButtonBase
          data-test="edit-charge-date-day-last"
          aria-pressed={isLastDaySelected}
          onClick={() => onSelectDay(LAST_DAY_OF_MONTH)}
          sx={(theme) => ({
            gridColumn: "span 5",
            justifySelf: "start",
            height: 36,
            paddingX: theme.spacing(1.5),
            borderRadius: "18px",
            gap: 0.5,
            ...theme.typography.body2,
            color: isLastDaySelected ? theme.palette.text.reverse.intense : theme.palette.text.neutral.moderate,
            backgroundColor: isLastDaySelected ? theme.palette.surface.brand.intense : "transparent",
            border:
              !isLastDaySelected && currentDay === LAST_DAY_OF_MONTH
                ? `1px solid ${theme.palette.border.neutral.quiet}`
                : "none",
          })}
        >
          {t("dashboard", "editChargeDateDialog.lastDayOption")}
          <Box
            component="span"
            sx={(theme) => ({
              color: isLastDaySelected ? theme.palette.text.reverse.intense : theme.palette.text.neutral.quiet,
            })}
          >
            {t("dashboard", "editChargeDateDialog.lastDayOptionDays")}
          </Box>
        </ButtonBase>
      )}
    </Box>
  );
};
